/**
 * Legal Footer - Site-wide footer with policy links
 * Links to Privacy Policy, Terms & Conditions, and Refund Policy
 * 
 * MODAL-AWARE: Uses ModalContext to hide when any modal is open.
 * This prevents the footer from overlapping modal CTAs (Buy Report, Choose Plan, etc.)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, RefreshCw, Mail, Heart, Info, Home, Sparkles, BarChart3, Globe } from 'lucide-react';
import { useModal } from '../contexts/ModalContext';

const LegalFooter = () => {
  const currentYear = new Date().getFullYear();
  
  // Get modal state - hide footer when any modal is open
  const { isAnyModalOpen } = useModal();

  // Don't render footer when any modal is open
  if (isAnyModalOpen) {
    return null;
  }

  return (
    <footer className="relative z-50 w-full bg-slate-900/95 border-t border-white/10 mt-auto backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Main footer content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Brand */}
          <div className="flex items-center gap-2 text-white/60">
            <img src="/logo-mobile.png" alt="JyotishPuraan" style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 4 }} />
            <span className="font-medium">Jyotish Puraan</span>
          </div>

          {/* Legal Links - All clickable with proper z-index */}
          <nav className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm">
            <a
              href="https://jyotishpuraan.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-main-site-link"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>JyotishPuraan.com</span>
            </a>

            <Link
              to="/"
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-home-link"
            >
              <Home className="w-3.5 h-3.5" />
              <span>Home</span>
            </Link>

            <Link
              to="/free-trial"
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-free-trial-link"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Free Trial</span>
            </Link>

            <Link
              to="/ashtakvarga"
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-ashtakvarga-link"
            >
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Ashtakvarga</span>
            </Link>

            <Link 
              to="/about" 
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-about-link"
            >
              <Info className="w-3.5 h-3.5" />
              <span>About Us</span>
            </Link>

            <Link 
              to="/privacy" 
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-privacy-link"
            >
              <Shield className="w-3.5 h-3.5" />
              <span>Privacy Policy</span>
            </Link>
            
            <Link 
              to="/terms" 
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-terms-link"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Terms & Conditions</span>
            </Link>
            
            <Link 
              to="/refund" 
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-refund-link"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Refund Policy</span>
            </Link>
            
            <Link 
              to="/contact" 
              className="flex items-center gap-1.5 text-white/60 hover:text-purple-400 transition-colors cursor-pointer"
              data-testid="footer-contact-link"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>Contact Us</span>
            </Link>
          </nav>
        </div>

        {/* Copyright */}
        <div className="mt-4 pt-4 border-t border-white/5 text-center">
          <p className="text-xs text-white/40 flex items-center justify-center gap-1">
            © {currentYear} Jyotish Puraan. Made with 
            <Heart className="w-3 h-3 text-red-400 fill-red-400" /> 
            in India
          </p>
          <p className="text-[10px] text-white/30 mt-1">
            Payments secured by Razorpay • For entertainment purposes only
          </p>
        </div>
      </div>
    </footer>
  );
};

export default LegalFooter;
